import { useQuery } from '@tanstack/react-query';
import type { SubscriptionDto } from '@cpm/shared';
import { Package } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client.js';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  statusTone,
} from '../components/ui.js';

export function SubscriptionsPage() {
  const navigate = useNavigate();
  const subs = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => api.get<SubscriptionDto[]>('/api/subscriptions/'),
  });

  return (
    <div>
      <PageHeader
        title="Subscriptions"
        description="Hosting subscriptions across all clients. Create new ones from a client's page."
      />
      <Card>
        {subs.isPending ? (
          <Spinner />
        ) : subs.error ? (
          <div className="p-5">
            <ErrorNote error={subs.error} />
          </div>
        ) : subs.data.length === 0 ? (
          <EmptyState
            icon={<Package className="size-10" />}
            title="No subscriptions yet"
            description="Open a client and create their first subscription to get started."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3 font-medium">Subscription</th>
                  <th className="px-5 py-3 font-medium">Client</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Coolify project</th>
                  <th className="px-5 py-3 font-medium">Health</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {subs.data.map((sub) => (
                  <tr
                    key={sub.id}
                    className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => void navigate(`/subscriptions/${sub.id}`)}
                  >
                    <td className="px-5 py-3 font-medium">{sub.name}</td>
                    <td className="px-5 py-3">
                      <Link
                        to={`/clients/${sub.clientId}`}
                        className="text-slate-600 hover:text-brand-600 dark:text-slate-300"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {sub.clientName}
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{sub.planName}</td>
                    <td className="px-5 py-3">
                      {sub.coolifyProjectUuid ? (
                        <Badge tone="blue">linked</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {/* Rolled up by the sync job (ADR-0010). */}
                      <Badge tone={statusTone(sub.health)}>{sub.health}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <span className="flex flex-wrap gap-1.5">
                        <Badge tone={statusTone(sub.status)}>{sub.status}</Badge>
                        {sub.overQuota && <Badge tone="red">over quota</Badge>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
